import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';
import './AdminPages.css';

const PartnerKycRequests = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}` : '';
      const res = await axios.get(`${API_URL}/kyc/admin/requests${q}`, { headers });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load KYC requests');
    } finally {
      setLoading(false);
    }
  }, [filter, headers]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (row, status) => {
    const admin_note = status === 'rejected' ? window.prompt('Rejection reason (optional):', row.admin_note || '') || '' : '';
    setActionLoadingId(row.id);
    try {
      await axios.put(`${API_URL}/kyc/admin/requests/${row.id}/status`, { status, admin_note }, { headers });
      toast.success(`KYC ${status}`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Status update failed');
    } finally {
      setActionLoadingId(null);
    }
  };

  const getFileUrl = (fileName) => `${API_URL.replace('/api', '')}/uploads/${fileName}`;

  return (
    <div className="admin-page">
      <div className="page-header flex-header">
        <div>
          <h1>Partner KYC Requests</h1>
          <p>Review uploaded KYC documents and approve or reject requests.</p>
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ minWidth: 170 }}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div className="admin-card table-section">
        {loading ? <div className="skeleton" style={{ height: 250 }} /> : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Partner</th>
                <th>Document Type</th>
                <th>Document</th>
                <th>Status</th>
                <th>Admin Note</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>#{r.id}</td>
                  <td>
                    <div className="table-cell-title">
                      <strong>{r.partner_name || '-'}</strong>
                      <span>{r.partner_email || '-'}</span>
                    </div>
                  </td>
                  <td>{r.document_type_name}</td>
                  <td>
                    <a href={getFileUrl(r.document_file)} target="_blank" rel="noreferrer">View Document</a>
                  </td>
                  <td>
                    <span className={`badge-role ${r.status === 'approved' ? 'partner' : r.status === 'rejected' ? 'admin' : 'user'}`}>
                      {r.status}
                    </span>
                  </td>
                  <td>{r.admin_note || '—'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-icon" disabled={actionLoadingId === r.id || r.status === 'approved'} onClick={() => setStatus(r, 'approved')} title="Approve">
                        <FiCheckCircle />
                      </button>
                      <button className="btn-icon text-danger" disabled={actionLoadingId === r.id || r.status === 'rejected'} onClick={() => setStatus(r, 'rejected')} title="Reject">
                        <FiXCircle />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PartnerKycRequests;
